/**
 * Channels, formats, and which channels each format is for.
 *
 * A mirror of `service/server/content_server/formats.py`, which is the source
 * of truth: the server validates against it and fills in the same defaults
 * when a POST leaves them out. `formats.test.ts` reads that file and fails when
 * the two disagree, so a format added there cannot be forgotten here. The
 * labels and the channel icons are the app's own.
 *
 * - `short`: the vertical reel, one file for Instagram, TikTok and YouTube Shorts.
 * - `youtube`: a long-form 16:9 YouTube video.
 * - `linkedin`: a topic Martin writes up himself. Nothing here drafts the post.
 */

export const CHANNELS = ['short', 'youtube', 'linkedin'] as const
export type Channel = (typeof CHANNELS)[number]

export const CHANNEL_LABEL: Record<Channel, string> = {
  short: 'Shorts',
  youtube: 'YouTube',
  linkedin: 'LinkedIn',
}

/** The channels that are filmed, and so the ones that can hold a video file. */
export const FILMED_CHANNELS = ['short', 'youtube'] as const
/** LinkedIn is written, so a LinkedIn-only idea never reaches Film and never holds a video. */
export type FilmedChannel = (typeof FILMED_CHANNELS)[number]

/**
 * What a video carries when it goes out, the channel it belongs to, and how long
 * each may be on the platform strictest about it: a Short's one caption (Instagram,
 * TikTok, YouTube Shorts) and a long video's YouTube title and description. Never
 * LinkedIn's. Mirrors `POST_TEXT` and `POST_TEXT_CHANNEL` in the server's `formats.py`.
 */
export const POST_TEXT = {
  short_caption: { channel: 'short', limit: 2200 },
  youtube_title: { channel: 'youtube', limit: 100 },
  youtube_description: { channel: 'youtube', limit: 5000 },
} as const satisfies Record<string, { channel: FilmedChannel; limit: number }>

export type PostTextField = keyof typeof POST_TEXT
export const POST_TEXT_FIELDS = Object.keys(POST_TEXT) as PostTextField[]

interface FormatSpec {
  /** What a new idea in this format is for, unless told otherwise. */
  channels: readonly Channel[]
  label: string
  /** One card, one spoken line, one reel: a batch of them films in one sitting. */
  volume?: true
}

/** In the order pickers show them, as on the server. */
export const FORMAT_CATALOG = {
  question: { channels: ['short'], label: 'question', volume: true },
  term: { channels: ['short'], label: 'term', volume: true },
  story: { channels: ['short'], label: 'story' },
  pov: { channels: ['short'], label: 'pov' },
  'two-ways': { channels: ['short'], label: 'two ways' },
  framework: { channels: ['short'], label: 'framework' },
  'free-thing': { channels: ['short'], label: 'free thing' },
  carousel: { channels: ['short'], label: 'carousel' },
  'deep-dive': { channels: ['youtube'], label: 'deep dive' },
  experiment: { channels: ['youtube'], label: 'experiment' },
  course: { channels: ['youtube'], label: 'course' },
  'start-over': { channels: ['youtube'], label: 'start over' },
  'case-study': { channels: ['youtube'], label: 'case study' },
  conversation: { channels: ['youtube'], label: 'conversation' },
  post: { channels: ['linkedin'], label: 'post' },
} as const satisfies Record<string, FormatSpec>

export type Format = keyof typeof FORMAT_CATALOG
export const FORMATS = Object.keys(FORMAT_CATALOG) as Format[]

export const FORMAT_LABEL = Object.fromEntries(
  FORMATS.map((format) => [format, FORMAT_CATALOG[format].label]),
) as Record<Format, string>

export const VOLUME_FORMATS: Format[] = FORMATS.filter(
  (format) => (FORMAT_CATALOG[format] as FormatSpec).volume,
)

/** The channels an idea in `format` is for when nobody said. */
export function channelsFor(format: Format): Channel[] {
  return [...FORMAT_CATALOG[format].channels]
}

/** The format listed first for `channel`: what an idea for it alone is, when the title cannot say. */
export function firstFormatFor(channel: Channel): Format {
  return FORMATS.find((format) =>
    (FORMAT_CATALOG[format].channels as readonly Channel[]).includes(channel),
  )!
}

/** The formats listed for any of `channels`, in catalogue order. */
export function formatsFor(channels: readonly Channel[]): Format[] {
  return FORMATS.filter((format) =>
    (FORMAT_CATALOG[format].channels as readonly Channel[]).some((c) => channels.includes(c)),
  )
}

/** Channels in the canonical order, each once. */
export function orderChannels(channels: Iterable<Channel>): Channel[] {
  const wanted = new Set(channels)
  return CHANNELS.filter((channel) => wanted.has(channel))
}

/** Whether this channel is one that can hold a video file. */
export function isFilmedChannel(channel: Channel): channel is FilmedChannel {
  return (FILMED_CHANNELS as readonly Channel[]).includes(channel)
}

/** Whether an idea could ever be filmed: it is for at least one filmed channel. */
export function isFilmable(idea: { channels: readonly Channel[] }): boolean {
  return idea.channels.some(isFilmedChannel)
}
